import { AGENTS } from "./content";

/**
 * The canvas's `AI Agents` screen — the only interactive section.
 *
 * Six buttons on the left drive one sticky detail panel on the right. The six
 * per-state colours below are lifted straight from `renderVals()`; the canvas
 * derives them per agent on every render rather than expressing selection as a
 * class, and keeping that shape means the selected/unselected values can be
 * compared against the source line by line.
 */

const ON = {
  bc: "rgba(var(--band-acc-rgb),.55)",
  bg: "rgba(var(--band-acc-rgb),.09)",
  // --band-acc, not --band-acc-h: the hover value measures 4.43:1 on the
  // light band, just under the 4.5 floor for this 14.5px name. This one
  // clears in both (6.92 light / 13.04 dark). It was a bare #9DF1FF, which
  // is the DARK linkh, so the selected agent was pale cyan on cream.
  fg: "var(--band-acc)",
  gBg: "rgba(var(--band-acc-rgb),.16)",
  gFg: "var(--band-acc)",
  arrowCol: "var(--band-acc)",
};

const OFF = {
  bc: "rgba(var(--band-line-rgb),.14)",
  bg: "rgba(var(--band-line-rgb),.02)",
  fg: "rgba(var(--band-ink-rgb),.8)",
  gBg: "rgba(var(--band-line-rgb),.06)",
  gFg: "rgba(var(--band-ink-rgb),.6)",
  arrowCol: "rgba(var(--band-ink-rgb),.62)",
};

interface AgentsProps {
  selectedId: string;
  onPick: (id: string) => void;
}

export default function Agents({ selectedId, onPick }: AgentsProps) {
  // The canvas falls back to index 5 when no id matches, rather than rendering
  // an empty panel.
  const sel = AGENTS.find((a) => a.id === selectedId) ?? AGENTS[5];

  return (
    <section
      id="agents"
      className="dc-seam site-gutter"
      style={{
        position: "relative",
        overflow: "clip",
        background: "var(--band)",
        color: "var(--band-ink)",
        paddingTop: 118,
        paddingBottom: 118,
      }}
    >
      {/* List beside a sticky panel from tablet up; stacked on a phone, with
          the panel static under the list (global.css, T101026002). */}
      <div className="home-agents-layout">
        <div>
          <div data-rv="">
            <div
              style={{
                fontFamily: "'Manrope', sans-serif",
                fontSize: 11.5,
                letterSpacing: ".16em",
                textTransform: "uppercase",
                color: "var(--band-acc)",
                marginBottom: 18,
              }}
            >
              <span className="dc-mark" aria-hidden="true" /> AI Agents
            </div>
            <h2
              style={{
                margin: "0 0 16px",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "clamp(28px, 3.2vw, 42px)",
                letterSpacing: "-0.02em",
                lineHeight: 1.14,
              }}
            >
              A specialist for every function
            </h2>
            <p
              style={{
                margin: "0 0 34px",
                fontSize: 16,
                color: "rgba(var(--band-ink-rgb),.68)",
              }}
            >
              Every agent works inside the permissions of the role it serves.
              Choose one to see the intents it handles.
            </p>
          </div>
          <div
            data-rv=""
            style={{ display: "flex", flexDirection: "column", gap: 8 }}
          >
            {AGENTS.map((ag) => {
              const c = ag.id === selectedId ? ON : OFF;
              return (
                <button
                  key={ag.id}
                  type="button"
                  onClick={() => onPick(ag.id)}
                  className="dc-h-agent"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 14,
                    textAlign: "left",
                    cursor: "pointer",
                    fontFamily: "'Manrope', sans-serif",
                    fontSize: 14.5,
                    fontWeight: 600,
                    padding: "13px 18px",
                    borderRadius: 12,
                    border: `1px solid ${c.bc}`,
                    background: c.bg,
                    color: c.fg,
                    transition: "border-color .2s, background .2s, color .2s",
                  }}
                >
                  <span
                    style={{
                      width: 30,
                      height: 30,
                      borderRadius: 9,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 14,
                      background: c.gBg,
                      color: c.gFg,
                    }}
                  >
                    {ag.glyph}
                  </span>
                  {ag.name}
                  <span
                    style={{
                      marginLeft: "auto",
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 11,
                      color: c.arrowCol,
                    }}
                  >
                    &rarr;
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div data-rv="" className="home-agents-panel">
          <div
            className="home-agents-card"
            style={{
              background: "var(--band-surface)",
              border: "1px solid rgba(var(--band-acc-rgb),.25)",
              borderRadius: 18,
              minHeight: 380,
              display: "flex",
              flexDirection: "column",
              boxShadow: "0 30px 70px rgba(0,0,0,.4)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 18,
              }}
            >
              <span
                style={{
                  width: 52,
                  height: 52,
                  borderRadius: 14,
                  background: "rgba(var(--band-acc-rgb),.13)",
                  border: "1px solid rgba(var(--band-acc-rgb),.3)",
                  color: "var(--band-acc)",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 22,
                }}
              >
                {sel.glyph}
              </span>
              <h3
                style={{
                  margin: 0,
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 600,
                  fontSize: 21,
                  letterSpacing: "-0.01em",
                }}
              >
                {sel.name}
              </h3>
            </div>
            <p
              style={{
                margin: "0 0 26px",
                fontSize: 15.5,
                color: "rgba(var(--band-ink-rgb),.7)",
              }}
            >
              {sel.desc}
            </p>
            <div
              style={{
                borderTop: "1px solid rgba(var(--band-line-rgb),.1)",
                paddingTop: 20,
              }}
            >
              <div
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: 11,
                  letterSpacing: ".16em",
                  textTransform: "uppercase",
                  color: "rgba(var(--band-ink-rgb),.62)",
                  marginBottom: 14,
                }}
              >
                Sample intents
              </div>
              <div
                style={{ display: "flex", flexDirection: "column", gap: 8 }}
              >
                {sel.intents.map((it) => (
                  <div
                    key={it}
                    className="dc-h-intent"
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      gap: 12,
                      fontFamily: "'Manrope', sans-serif",
                      fontSize: 13.5,
                      color: "rgba(var(--band-ink-rgb),.85)",
                      background: "rgba(var(--band-line-rgb),.04)",
                      border: "1px solid rgba(var(--band-line-rgb),.07)",
                      borderRadius: 10,
                      padding: "12px 16px",
                      transition: "border-color .2s, background .2s",
                    }}
                  >
                    <span style={{ color: "var(--band-acc)" }}>&gt;</span>
                    {it}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
